/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { Fragment, useState } from 'react'
import { useDebouncedCallback } from 'utils/hooks/useDebounce'
import { Listbox, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import TablePageSizeItemsList from './TablePageSizeComponents/TablePageSizeItemsList'

interface TablePageSizeProps {
    pageSizes: number[]
    selected?: number
    onChange: (page: number) => void
    disabled?: boolean
    idPrefix?: string
    optionLabels?: Record<number, string>
}

const TablePageSize = (props: TablePageSizeProps) => {
    const { t } = useTranslation()
    const resolvedSelected = props.selected ?? 10
    const [internalValue, setInternalValue] = useState<number>(resolvedSelected)
    const [prevSelected, setPrevSelected] = useState(props.selected)
    const debouncedChangeHandler = useDebouncedCallback((data) => props.onChange(data), 300)

    if (props.selected !== prevSelected) {
        setPrevSelected(props.selected)
        setInternalValue(resolvedSelected)
    }

    const getOptionLabel = (value?: number) => {
        if (typeof value !== 'number') {
            return ''
        }

        return props.optionLabels?.[value] ?? String(value)
    }

    const onChangeHandler = (newPage: number) => {
        setInternalValue(newPage)
        debouncedChangeHandler(newPage)
    }

    return (
        <div className={clsx('TablePageSize relative z-10 mr-1.5')}>
            <Listbox disabled={props.disabled} value={internalValue} onChange={onChangeHandler}>
                {({ open }) => (
                    <>
                        <Listbox.Button
                            id={props.idPrefix ? `${props.idPrefix}-size-selector` : undefined}
                            className={clsx(
                                'TableFoot-control',
                                'relative cursor-pointer rounded-lg gap-1.5 px-3 transition-all duration-200 shadow-sm border whitespace-nowrap',
                                'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:shadow-md',
                                'dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-800',
                                open
                                    ? 'ring-2 ring-blue-500/20 border-blue-500 text-blue-500 shadow-md'
                                    : 'text-gray-700 dark:text-gray-200'
                            )}
                        >
                            <span className="opacity-60 font-medium">{t('Show')}</span>
                            <span className={clsx('font-bold')}>
                                {getOptionLabel(internalValue)}
                            </span>
                            <span
                                className={clsx(
                                    'pointer-events-none ml-0.5 flex items-center text-gray-400 group-hover:text-blue-500',
                                    open && 'text-blue-500'
                                )}
                            >
                                <ChevronUpDownIcon className={clsx('h-4 w-4')} />
                            </span>
                        </Listbox.Button>
                        <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options
                                className={clsx(
                                    'absolute bottom-full mb-1 z-50 w-fit min-w-full overflow-auto overscroll-contain rounded-xl bg-white py-1 text-xs shadow-2xl ring-1 ring-black/5 focus:outline-none max-h-[min(15rem,calc(100dvh-8rem))] dark:bg-gray-950 dark:ring-white/10'
                                )}
                            >
                                <TablePageSizeItemsList
                                    items={props.pageSizes}
                                    optionLabels={props.optionLabels}
                                />
                            </Listbox.Options>
                        </Transition>
                    </>
                )}
            </Listbox>
        </div>
    )
}

export default TablePageSize
